/**
 * Created by Ning Jiang on 2021-06-09.
 */
import {TombstoneRipSymbol} from "./TombstoneRipSymbol";
import {TombstoneRipSymbolState} from "./TombstoneRipSymbolConfig";
import {gsap, TimelineLite} from "gsap";
import {TombstoneRip} from "../TombstoneRip";
import {SpineTween} from "@nolimitcity/slot-game/bin/animation/SpineTween";
import {Layers, TombstoneRipLayers} from "../view/TombstoneRipLayers";
import {TimelineSprite} from "@nolimitcity/slot-launcher/bin/animation/TimelineSprite";
import {MultiSprites} from "../../core/animation/MultiSprites";
import {ImgLoader} from "@nolimitcity/slot-launcher/bin/loader/ImgLoader";

export class TombstoneScatter extends TombstoneRipSymbol {

    private _symbolSpine:PIXI.spine.Spine;
    private _bloodEnd:PIXI.Sprite;

    private _nearWinTl:TimelineLite | null;
    private _nearWinEffects:PIXI.spine.Spine | null;
    private _battleGlow:TimelineSprite;
    private _nearWinPlaying:boolean = false;

    private _winTl:TimelineLite | null;
    private _winEffects:PIXI.spine.Spine | null;
    private _overlay:PIXI.Sprite;

    constructor(symbolName:string, reelId:number, initState:TombstoneRipSymbolState) {
        super(symbolName, reelId, initState);
    }


    protected initAnimation() {
        super.initAnimation();
        this._overlay = new PIXI.Sprite(ImgLoader.getImgTexture("S1_overlay"));
        this._overlay.anchor.set(0.5,0.5);
        this.addChild(this._overlay);
    }

    protected updateLayers() {
        super.updateLayers();
        if(this._symbolSpine) {
            this.addChild(this._symbolSpine);
        }
        if(this._scatterFrame) {
            this.addChild(this._scatterFrame);
        }
        if(this._bloodEnd) {
            this.addChild(this._bloodEnd);
        }
        if(this._battleGlow) {
            this.addChild(this._battleGlow);
        }
        if(this._rip) {
            this.addChild(this._rip);
        }
        if(this._overlay) {
            this.addChild(this._overlay);
        }

    }

    protected getStateColorChangeAnimation(tint:number, tintSaturation:number, tintDuration:number):TimelineLite {
        const tl = super.getStateColorChangeAnimation(tint, tintSaturation, tintDuration);
        if(this._symbolSpine && this._symbolSpine.visible) {
            tl.add(() => {this._symbolSpine.tint = tint}, 0);
        }

        return tl;
    }

    protected addSplitSymbolAnimation(tl:TimelineLite, oldSymbolSize:number, type:number) {
        if(this.symbolName === "S2") {
            // This will never happen.
            return;
        }

        const oldPart = this._parts[0];
        this._parts = [];
        const newPart = new MultiSprites();
        newPart.position.set(this._centerPosition.x, this._centerPosition.y);
        newPart.alpha = 0;
        this.addChild(newPart);
        this._parts.push(newPart);

        tl.add(gsap.to(oldPart, {
            duration : 0.5, alpha : 0, onComplete : () => {
                this.removeChild(oldPart);
                oldPart.destroy();
            }
        }), 0.2);
        if(this._symbolSpine) {
            tl.add(gsap.to(this._symbolSpine, {
                duration : 0.5, alpha : 0, onComplete : () => {
                    this._symbolSpine.visible = false;
                }
            }), 0.2);
        }
        if(this._bloodEnd) {
            tl.add(gsap.to(this._bloodEnd, {
                duration : 0.5, alpha : 0, onComplete : () => {
                    this._bloodEnd.visible = false;
                }
            }), 0.2);
        }
        tl.add(this.getStateAnimation(TombstoneRipSymbolState.NORMAL, {duration : 0, alpha : 1}, {
            duration : 0,
            alpha : 0
        }), 0);
        tl.add(gsap.to(newPart, {duration : 0.4, alpha : 1}), 0.3);
        tl.add(this.getPartDaggerSplitAnimation(newPart), this._splitSplashDelay);
    }

    // in scatter, this landing is only played on restore init.
    public playLanding(init:boolean = false):TimelineLite {
        this.prepareSpine();
        const tl = new TimelineLite();
        tl.add(() => {
            this.updateLayers();
            this._symbolSpine.visible = true;
        });
        if(init) {
            tl.add(() => {
                // this._symbolSpine.state.timeScale = 1.5;
                let trackEntry = this._symbolSpine.state.setAnimation(0, "landing", false);
                trackEntry.trackTime = trackEntry.trackEnd * 0.5;
            });
            tl.add(gsap.to(this, {duration : 0.01}));
        } else {
            tl.add(new SpineTween(this._symbolSpine, 0, `landing`));
        }
        return tl;
    }

    public playNoose(delay:number = 0):TimelineLite {
        this.prepareSpine();
        const tl = new TimelineLite();
        tl.add(() => {
            this.updateLayers();
            this._symbolSpine.visible = true;
            // this._symbolSpine.state.timeScale = 1;
        }, delay);

        tl.add(new SpineTween(this._symbolSpine, 0, `nooseDown`));

        return tl;
    }

    public playBlood():TimelineLite {
        const stagePosition = this.getStagePosition();
        const blood = new PIXI.spine.Spine(TombstoneRip.assetLoader.getSpineAsset("S1Effects"));
        blood.autoUpdate = true;
        blood.position.set(stagePosition.x, stagePosition.y);
        const bloodTl = new TimelineLite();
        bloodTl.add([
            () => {
                TombstoneRipLayers.getLayer(Layers.NEAR_WIN)!.addChild(blood)
                this._overlay.position.set(stagePosition.x, stagePosition.y);
                TombstoneRipLayers.getLayer(Layers.NEAR_WIN)!.addChild(this._overlay)
            },
            new SpineTween(blood, 0, "attention")
        ], 0.1);
        bloodTl.add(() => {
            if(!this.destroyed) {
                this._bloodEnd = new PIXI.Sprite(TombstoneRip.assetLoader.getTextures("S1/s1Static")[0]);
                this._bloodEnd.anchor.set(0.5);
                this.updateLayers();
            }
            this._overlay.position.set(0,0);
            this.addChild(this._overlay);
            blood.state.setEmptyAnimation(0, 0);
            if(blood.parent) {
                blood.parent.removeChild(blood);
            }
            blood.destroy();
        });

        return bloodTl;
    }

    private prepareSpine():void {
        if(!this._symbolSpine) {
            this._symbolSpine = new PIXI.spine.Spine(TombstoneRip.assetLoader.getSpineAsset(`${this.symbolName}`));
            this._symbolSpine.autoUpdate = true;
            this._symbolSpine.position.set(this._centerPosition.x, this._centerPosition.y);
            this._symbolSpine.visible = false;
        }
    }

    public playNearWin():Promise<any> {
        this._nearWinPlaying = true;
        if(!this._nearWinEffects) {
            const stagePosition = this.getStagePosition();
            this._nearWinEffects = new PIXI.spine.Spine(TombstoneRip.assetLoader.getSpineAsset("S1Effects"));
            this._nearWinEffects.autoUpdate = true;
            this._nearWinEffects.visible = false;
            this._nearWinEffects.position.set(stagePosition.x, stagePosition.y);
        }
        if(!this._battleGlow) {
            this._battleGlow = new TimelineSprite(TombstoneRip.assetLoader.getTextures("scatter/loop/battle_"), 15);
            this._battleGlow.blendMode = PIXI.BLEND_MODES.ADD;
            this._battleGlow.anchor.set(0.5);
            this._battleGlow.hide();
            this.updateLayers();
        }
        const nearWinTl = new TimelineLite();
        nearWinTl.add(gsap.to(this.scale, {x : 1.2, y : 1.2, duration : 0.033}), 0.767);
        nearWinTl.add(gsap.to(this.scale, {x : 1.05, y : 1.05, duration : 0.2}));
        nearWinTl.add(() => {
            if(this._nearWinPlaying) {
                this._battleGlow.playLoop();
                this._battleGlow.show();
                TombstoneRipLayers.getLayer(Layers.NEAR_WIN)!.addChild(this._nearWinEffects!);
                this._nearWinEffects!.visible = true;
                this._nearWinEffects!.state.setAnimation(0, "scatterLooping", true);
            }
        });

        this._nearWinTl = nearWinTl;
        return Promise.resolve();
    }

    public playWin(loop:boolean, delay = 0):Promise<any> {
        this.stopNearWin();

        if(this._winTl && this._winTl.isActive()) {
            this._winTl.pause();
            this._winTl.kill();
        }

        if(!this._winEffects) {
            const stagePosition = this.getStagePosition();
            this._winEffects = new PIXI.spine.Spine(TombstoneRip.assetLoader.getSpineAsset("SEffects"));
            this._winEffects.autoUpdate = true;
            this._winEffects.visible = false;
            this._winEffects.position.set(stagePosition.x, stagePosition.y);
        }

        //this._winEffects.addChild(this._overlay);

        const winTl = new TimelineLite();
        winTl.add(() => {
            TombstoneRipLayers.getLayer(Layers.NEAR_WIN)!.addChild(this._winEffects!);
            this._winEffects!.visible = true;
        }, delay);

        const aniName = `burn${this.symbolName}`;
        winTl.add([
            () => {this._winEffects!.state.setAnimation(0, aniName, true)},
            gsap.to(this, {duration : this._winEffects!.spineData.findAnimation(aniName).duration})
        ]);

        this._winTl = winTl;
        return Promise.resolve();
    }

    public stopNearWin():void {
        this._nearWinPlaying = false;
        if(this._nearWinTl && this._nearWinTl.isActive()) {
            this._nearWinTl.progress(1);
        }
        this._nearWinTl = null;
        if(this._nearWinEffects) {
            this._nearWinEffects.state.setEmptyAnimation(0, 0);
            this._nearWinEffects.visible = false;
            if(this._nearWinEffects.parent) {
                this._nearWinEffects.parent.removeChild(this._nearWinEffects);
            }
            this._nearWinEffects.destroy();
            this._nearWinEffects = null;
        }
        if(this._battleGlow) {
            this._battleGlow.stopLoop();
            this._battleGlow.hide();
        }
        gsap.to(this.scale, {x : 1, y : 1, duration : 0.1});
    }

    public stopWin():void {
        if(this._winTl && this._winTl.isActive()) {
            this._winTl.pause();
            this._winTl.kill();
        }
        if(this._winEffects) {
            this._winEffects.state.setEmptyAnimation(0, 0);
            this._winEffects.visible = false;
            if(this._winEffects.parent) {
                this._winEffects.parent.removeChild(this._winEffects);
            }
            this._winEffects.destroy();
            this._winEffects = null;
        }
    }

    public onRemove() {
        this.stopNearWin();
        this.stopWin();
        super.onRemove();
    }
}